import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import axios from 'axios';
import CONFIG from '../config';
import { getToken } from '../utils/auth';

const OrderDetails = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const isAr = i18n.language?.startsWith('ar');
    const { formatPrice } = useCurrency();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const token = getToken();
                const res = await axios.get(`${CONFIG.API_URL}/api/orders/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOrder(res.data);
            } catch (err) {
                console.error('Failed to fetch order details:', err);
                navigate('/profile/orders');
            } finally {
                setLoading(false);
            }
        };
        fetchOrderDetails();
    }, [id, navigate]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-amber-500 shadow-amber-200';
            case 'processing': return 'bg-blue-500 shadow-blue-200';
            case 'shipped': return 'bg-indigo-500 shadow-indigo-200';
            case 'delivered': return 'bg-green-500 shadow-green-200';
            case 'cancelled': return 'bg-red-500 shadow-red-200';
            default: return 'bg-gray-500 shadow-gray-200';
        }
    };

    if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-12 h-12 border-4 border-gray-100 border-t-brand-primary rounded-full animate-spin" /></div>;
    if (!order) return null;

    return (
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12 lg:py-20 animate-fade-in">
            <button
                onClick={() => navigate('/profile/orders')}
                className="mb-10 text-gray-400 hover:text-brand-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all"
            >
                <i className={`fas fa-arrow-${isAr ? 'right' : 'left'}`}></i>
                {t('orders.back_to_list', 'Back to My Orders')}
            </button>

            <div className="bg-white rounded-[40px] border border-gray-100 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-10 lg:p-16 bg-gray-50/50 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                    <div>
                        <span className="inline-block px-4 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-[10px] font-black uppercase tracking-[0.2em] mb-4">
                            {t('orders.order_placed', 'Order Placed')}
                        </span>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-2">#{order.id.split('_').pop().toUpperCase()}</h1>
                        <p className="text-gray-400 font-bold text-sm">
                            {new Date(order.created_at).toLocaleDateString(isAr ? 'ar-EG' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-right">
                            <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{t('orders.current_status', 'Current Status')}</p>
                            <p className="font-black text-gray-900 text-lg uppercase tracking-tight">{t(`orders.status_${order.status}`, order.status)}</p>
                        </div>
                        <div className={`w-4 h-4 rounded-full shadow-lg ${getStatusColor(order.status)}`} />
                    </div>
                </div>

                <div className="p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-3 gap-16">
                    {/* Items */}
                    <div className="lg:col-span-2 space-y-8">
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight mb-8">{t('orders.items_ordered', 'Items Ordered')}</h2>
                        <div className="space-y-6">
                            {order.items?.map((item) => (
                                <div key={item.id} className="flex gap-6 items-center">
                                    <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 shadow-sm shrink-0">
                                        <img src={item.imageUrl || '/placeholder-furniture.jpg'} alt={isAr ? item.name_ar : item.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-black text-gray-900 group-hover:text-brand-primary transition-colors">
                                            {isAr && item.name_ar ? item.name_ar : item.name}
                                        </h4>
                                        <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">
                                            Qty: {item.quantity} × {formatPrice({ price: item.price_at_time, currency: order.currency })}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <span className="font-black text-gray-900">
                                            {formatPrice({ price: item.price_at_time * item.quantity, currency: order.currency })}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sidebar Details */}
                    <div className="space-y-12">
                        {/* Summary */}
                        <div className="bg-gray-50 rounded-3xl p-8 space-y-6">
                            <h3 className="font-black text-gray-900 uppercase tracking-widest text-[10px]">{t('orders.payment_summary', 'Payment Summary')}</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between text-sm font-bold text-gray-500">
                                    <span>{t('orders.subtotal', 'Subtotal')}</span>
                                    <span>{formatPrice({ price: order.total_amount, currency: order.currency })}</span>
                                </div>
                                <div className="flex justify-between text-sm font-bold text-gray-500">
                                    <span>{t('orders.shipping', 'Shipping')}</span>
                                    <span className="text-green-500">{t('orders.free', 'FREE')}</span>
                                </div>
                                <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                                    <span className="font-black text-gray-900 text-lg">{t('orders.total', 'Total')}</span>
                                    <span className="font-black text-brand-primary text-2xl">{formatPrice({ price: order.total_amount, currency: order.currency })}</span>
                                </div>
                            </div>
                        </div>

                        {/* Info */}
                        <div className="space-y-8 px-4">
                            <div>
                                <h3 className="font-black text-gray-900 uppercase tracking-widest text-[10px] mb-4">{t('orders.shipping_address', 'Shipping Address')}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed font-medium">{order.shipping_address}</p>
                            </div>
                            <div>
                                <h3 className="font-black text-gray-900 uppercase tracking-widest text-[10px] mb-4">{t('orders.contact_details', 'Contact Details')}</h3>
                                <p className="text-gray-500 text-sm font-black tracking-tight">{order.contact_phone}</p>
                            </div>
                            <div>
                                <h3 className="font-black text-gray-900 uppercase tracking-widest text-[10px] mb-4">{t('orders.payment_method', 'Payment Method')}</h3>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                                        <i className="fas fa-credit-card text-xs"></i>
                                    </div>
                                    <p className="text-gray-900 text-sm font-black uppercase tracking-tighter">{order.payment_method}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetails;
